import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import NTEECodes from './NTEECodes';
// Import for handling modals
import { createPortal } from 'react-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EditableGiftCard = ({ gift, setGifts, gifts, handleStatusUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedGift, setEditedGift] = useState({ ...gift });
  const [nteeSearchTerm, setNteeSearchTerm] = useState('');
  const [filteredNteeCodes, setFilteredNteeCodes] = useState([]);
  const [showNteeDropdown, setShowNteeDropdown] = useState(false);
  const nteeDropdownRef = useRef(null);
  const [hasModifiedInput, setHasModifiedInput] = useState(false);
  const [showDenyDropdown, setShowDenyDropdown] = useState(false);
  const [selectedDenialReason, setSelectedDenialReason] = useState('');
  const denyDropdownRef = useRef(null);
  
  // ProPublica lookup state
  const [showProPublicaModal, setShowProPublicaModal] = useState(false);
  const [proPublicaSearchTerm, setProPublicaSearchTerm] = useState('');
  const [proPublicaSearchResults, setProPublicaSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null); // Add this at the top with your other hooks

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, [proPublicaSearchTerm]);

  // Available gift types
  const giftTypes = ['Major Gift', 'Grant', 'Matching Gifts', 'Annual', 'Planned / Estate/ Bequest Gift', 'Board Member',
        'Capital / Campaign Gift', 'Consecutive', 'Other / Unknown', 'Cumulative', 'Donor Advised Funds',
        'Endowment Gifts', 'Event Sponsorship', 'In Kind', 'Memorial', 'Program / Restricted', 'Staff', 'Volunteers'];
  
  // Make sure gift_type is properly initialized
  useEffect(() => {
    if (gift.gift_type && !editedGift.gift_type) {
      setEditedGift(prev => ({
        ...prev,
        gift_type: gift.gift_type
      }));
    }
  }, [gift.gift_type]);
  
  // Find NTEE description for a given code
  const getNteeDescription = (code) => {
    const nteeItem = NTEECodes.find(item => item.code === code);
    return nteeItem ? `${code} - ${nteeItem.description}` : code;
  };
  
  // Filter NTEE codes based on search term - search in both code and description
  useEffect(() => {
    if (nteeSearchTerm) {
      const filtered = NTEECodes.filter(item => 
        `${item.code} - ${item.description}`
          .toLowerCase()
          .includes(nteeSearchTerm.toLowerCase())
      );
      setFilteredNteeCodes(filtered);
    } else if (nteeSearchTerm === '') {
      setFilteredNteeCodes(NTEECodes);
    } else {
      setFilteredNteeCodes(NTEECodes);
    }
  }, [nteeSearchTerm]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Close NTEE dropdown when clicking outside
      if (nteeDropdownRef.current && !nteeDropdownRef.current.contains(event.target)) {
        setShowNteeDropdown(false);
      }
      
      // Close deny dropdown when clicking outside
      if (denyDropdownRef.current && !denyDropdownRef.current.contains(event.target)) {
        setShowDenyDropdown(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For gift_amount, convert to number
    if (name === 'gift_amount') {
      setEditedGift({
        ...editedGift,
        [name]: parseInt(value) || 0
      });
    } else {
      setEditedGift({
        ...editedGift,
        [name]: value
      });
    }
  };
  
  // ProPublica search function
  const handleProPublicaSearch = async () => {
    if (!proPublicaSearchTerm.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API_URL}/api/propublica/search`, {
        params: { query: proPublicaSearchTerm }
      });
      
      if (response.data && response.data.organizations) {
        setProPublicaSearchResults(response.data.organizations);
      } else {
        setProPublicaSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching ProPublica:', error);
      setProPublicaSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle organization selection from ProPublica results
  const handleOrganizationSelect = (organization) => {
    // Update form fields with selected organization data
    setEditedGift(prev => ({
      ...prev,
      recipient_name: organization.name || prev.recipient_name,
      recipient_city: organization.city || prev.recipient_city,
      recipient_state: organization.state || prev.recipient_state,
      recipient_zip: organization.zipcode || prev.recipient_zip,
      recipient_ein: organization.ein || prev.recipient_ein,
      recipient_ntee_code: organization.ntee_code || prev.recipient_ntee_code
    }));
    
    // Close the modal
    setShowProPublicaModal(false);
  };
  
  // Modal component for ProPublica search
  const ProPublicaModal = () => {
    if (!showProPublicaModal) return null;
    
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
          <h2 className="text-xl font-bold mb-4">ProPublica Nonprofit Lookup</h2>          
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              ref={inputRef}
              value={proPublicaSearchTerm}
              onChange={(e) => setProPublicaSearchTerm(e.target.value)}
              className="border p-2 rounded flex-1"
              placeholder="Enter organization name"
              onKeyDown={(e) => e.key === 'Enter' && handleProPublicaSearch()}
            />
            <button
              onClick={handleProPublicaSearch}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {proPublicaSearchResults.length > 0 ? (
            <div className="mt-4 max-h-96 overflow-y-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Name</th>
                    <th className="border p-2 text-left">Location</th>
                    <th className="border p-2 text-left">EIN</th>
                    <th className="border p-2 text-left">NTEE Code</th>
                    <th className="border p-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {proPublicaSearchResults.map((org) => (
                    <tr key={org.ein} className="hover:bg-gray-50">
                      <td className="border p-2">{org.name}</td>
                      <td className="border p-2">{`${org.city || ''}, ${org.state || ''} ${org.zipcode || ''}`}</td>
                      <td className="border p-2">{org.ein}</td>
                      <td className="border p-2">{org.ntee_code}</td>
                      <td className="border p-2">
                        <button
                          onClick={() => handleOrganizationSelect(org)}
                          className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            proPublicaSearchTerm && !isSearching && <p className="text-center py-4">No results found</p>
          )}
          
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowProPublicaModal(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };
  
  const handleSave = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_URL}/api/gifts/${gift.id}/edit`, {
        originalGift: gift,
        updatedGift: editedGift
      },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
      
      // Update local state with the edited values
      setGifts(gifts.map(g => 
        g.id === gift.id ? { ...editedGift } : g
      ));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating gift:', error);
      alert('Failed to update the gift information. Please try again.');
    }
  };

  return (
    <div className={`p-4 mb-4 rounded-lg border ${
      gift.status === 'approved' ? 'bg-green-50 border-green-200' :
      gift.status === 'denied' ? 'bg-red-50 border-red-200' :
      'bg-white border-gray-200'
    }`}>
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-semibold">
          {isEditing ? (
            <div className="flex gap-4 w-full">
              <input
                type="text"
                name="recipient_name"
                value={editedGift.recipient_name}
                onChange={handleChange}
                className="border p-1 rounded w-full min-w-[320px]"
              />
              <em><small> or... </small></em>
              <button
                onClick={() => {
                  setProPublicaSearchTerm(editedGift.recipient_name);
                  setShowProPublicaModal(true);
                }}
                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm whitespace-nowrap"
              >
                ProPublica Lookup
              </button>
            </div>
          ) : (
            gift.recipient_name
          )}
        </h3>
        <div className="flex items-center">
          {gift.status && (
            <span className={`px-2 py-1 text-sm rounded mr-2 ${
              gift.status === 'approved' ? 'bg-green-100 text-green-800' :
              'bg-red-100 text-red-800'
            }`}>
              {gift.status.charAt(0).toUpperCase() + gift.status.slice(1)}
            </span>
          )}
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="ml-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="font-medium">Donor Information:</p>
          {isEditing ? (
            <input
              type="text"
              name="donor_name"
              value={editedGift.donor_name}
              onChange={handleChange}
              className="border p-1 rounded w-full mb-2"
            />
          ) : (
            <p>{gift.donor_name}</p>
          )}
          
          <div className="mt-2">
            <p className="font-medium">Gift Details:</p>
            {isEditing ? (
              <>
                <div className="flex items-center mb-2">
                  <span className="mr-1">$</span>
                  <input
                    type="number"
                    name="gift_amount"
                    value={editedGift.gift_amount}
                    onChange={handleChange}
                    className="border p-1 rounded w-full"
                  />
                </div>
                
                <input
                  type="number"
                  name="gift_year"
                  value={editedGift.gift_year}
                  onChange={handleChange}
                  className="border p-1 rounded w-full mb-2"
                  min="1900"
                  max="2100"
                />
                
                <select
                  name="gift_type"
                  value={editedGift.gift_type || gift.gift_type || ''}
                  onChange={handleChange}
                  className="border p-1 rounded w-full"
                >
                  <option value="" disabled>Select gift type</option>
                  {giftTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <p>${gift.gift_amount.toLocaleString()}</p>
                <p>{gift.gift_year}</p>
                <p>{gift.gift_type}</p>
              </>
            )}
          </div>
        </div>
        <div>
          <p className="font-medium">Recipient Details:</p>
          {isEditing ? (
            <>
              <div className="grid grid-cols-6 gap-1 mb-2">
                <input
                  type="text"
                  name="recipient_city"
                  value={editedGift.recipient_city || ''}
                  onChange={handleChange}
                  className="border p-1 rounded col-span-3"
                  placeholder="City"
                />
                <input
                  type="text"
                  name="recipient_state"
                  value={editedGift.recipient_state || ''}
                  onChange={handleChange}
                  className="border p-1 rounded col-span-1"
                  placeholder="State"
                  maxLength="2"
                />
                <input
                  type="text"
                  name="recipient_zip"
                  value={editedGift.recipient_zip || ''}
                  onChange={handleChange}
                  className="border p-1 rounded col-span-2"
                  placeholder="Zip"
                />
              </div>
            </>
          ) : (
            <p>{gift.recipient_city}, {gift.recipient_state} {gift.recipient_zip}</p>
          )}
          
          <div className="mt-2">
            {isEditing ? (
              <>
                <div className="mb-2">
                  <span className="block text-xs text-gray-600">EIN:</span>
                  <input
                    type="text"
                    name="recipient_ein"
                    value={editedGift.recipient_ein || ''}
                    onChange={handleChange}
                    className="border p-1 rounded w-full"
                    placeholder="EIN"
                  />
                </div>
                <div className="relative" ref={nteeDropdownRef}>
                  <span className="block text-xs text-gray-600">NTEE Code:</span>
                  <input
                    type="text"
                    name="recipient_ntee_code"
                    value={hasModifiedInput ? nteeSearchTerm ?? '' : (editedGift.recipient_ntee_code ? 
                      getNteeDescription(editedGift.recipient_ntee_code)
                      : '')}
                    onChange={(e) => {
                      setNteeSearchTerm(e.target.value);
                      setHasModifiedInput(true);
                      setShowNteeDropdown(true);
                    }}
                    onClick={() => setShowNteeDropdown(true)}
                    className="border p-1 rounded w-full"
                    placeholder="Search NTEE Code or Description"
                  />
                  {showNteeDropdown && (
                    <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded shadow-lg">
                      {filteredNteeCodes.length > 0 ? (
                        filteredNteeCodes.map((ntee) => (
                          <div
                            key={ntee.code}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={(e) => {
                              setEditedGift({
                                ...editedGift,
                                recipient_ntee_code: ntee.code
                              });
                              setNteeSearchTerm(e.target.innerText);
                              setShowNteeDropdown(false);
                            }}
                          >
                            {`${ntee.code} - ${ntee.description}`}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500">No matching NTEE codes found</div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="mt-2">EIN: {gift.recipient_ein}</p>
                <p>{getNteeDescription(gift.recipient_ntee_code)}</p>
              </>
            )}
          </div>
        </div>
      </div>
      
      {isEditing && (
        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={() => setIsEditing(false)}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Save
          </button>
        </div>
      )}

      {(!gift.status || gift.status === 'pending') && !isEditing && (
        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={() => handleStatusUpdate(gift.id, 'approved')}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
          >
            Approve
          </button>
          <div className="relative" ref={denyDropdownRef}>
            <button
              onClick={() => setShowDenyDropdown(!showDenyDropdown)}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 flex items-center"
            >
              Deny
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            
            {showDenyDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-300 rounded shadow-lg z-10">
                <div className="py-1">
                  {['Unusable Data', 'Irrelevant Data', 'Crowd Funding', 'Non-Specific', 'Other'].map((reason) => (
                    <button
                      key={reason}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      onClick={() => {
                        handleStatusUpdate(gift.id, 'denied', reason);
                        setShowDenyDropdown(false);
                        setSelectedDenialReason(reason);
                      }}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Render the ProPublica lookup modal */}
      <ProPublicaModal />
    </div>
  );
};

export default EditableGiftCard;